import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DebtorPageDto, DebtorSearchCriteria, DebtorService } from '../debtor.service';
import { DebtorSearchResultDto, DebtorDetailDto } from '../models/debtor.model';
import { MockDataLoader } from './mock-data-loader.service';
import { ActionMockStateService } from './action-mock-state.service';
import { MockAccessScopeService } from './mock-access-scope.service';

interface DebtorsFixture { items: DebtorSearchResultDto[] }

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** Normalise les variantes marocaines +212…, 0… et numéro national sans 0. */
export function normalizePhoneForSearch(value: string): string {
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('00212')) digits = digits.slice(5);
  else if (digits.startsWith('212')) digits = digits.slice(3);
  if (digits.length === 10 && digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

/** CA-CLI-01 — prédicat partagé et testable pour tous les champs du SFD. */
export function matchesDebtorQuery(debtor: DebtorSearchResultDto, query: string): boolean {
  const q = normalizeSearchText(query);
  const textValues = [
    debtor.debtorName,
    debtor.externalReference ?? debtor.debtorId,
    debtor.debtorId,
    debtor.ice,
    debtor.cin,
    debtor.city,
    ...(debtor.contractReferences ?? []),
  ];
  if (textValues.some(value => normalizeSearchText(value ?? '').includes(q))) return true;

  const queryPhone = normalizePhoneForSearch(query);
  const debtorPhone = normalizePhoneForSearch(debtor.mainPhone);
  return queryPhone.length >= 8 && debtorPhone.endsWith(queryPhone);
}

@Injectable()
export class DebtorMockService extends DebtorService {
  private readonly mockData = inject(MockDataLoader);
  private readonly actionState = inject(ActionMockStateService);
  private readonly accessScope = inject(MockAccessScopeService);

  search(criteria: DebtorSearchCriteria): Observable<DebtorPageDto> {
    return this.mockData.load<DebtorsFixture>('debtors').pipe(
      map(fixture => this.applyFilters(fixture.items, criteria)),
    );
  }

  getDebtor(debtorId: string): Observable<DebtorDetailDto> {
    return this.mockData.load<DebtorDetailDto>('debtor-detail').pipe(
      map(detail => {
        const actions = this.actionState.getForDebtor(debtorId);
        if (!actions.length) return detail;
        const visibleFixtureInteractions = detail.interactions.filter(interaction =>
          this.accessScope.canAccessCase({ agentId: interaction.agentId }),
        );
        return {
          ...detail,
          interactions: [
            ...actions.map(a => ({
              ...this.actionState.toInteraction(a),
              actionType: a.type,
              agentId: a.createdBy,
            })),
            ...visibleFixtureInteractions,
          ],
        };
      }),
    );
  }

  private applyFilters(items: DebtorSearchResultDto[], c: DebtorSearchCriteria): DebtorPageDto {
    let result = [...items];

    if (c.query && c.query.length >= 3) {
      result = result.filter(debtor => matchesDebtorQuery(debtor, c.query!));
    }

    if (c.clientType)  result = result.filter(d => d.clientType  === c.clientType);
    if (c.status)      result = result.filter(d => d.status      === c.status);
    if (c.riskSegment) result = result.filter(d => d.riskSegment === c.riskSegment);
    if (c.creditorId)  result = result.filter(d => d.creditorId  === c.creditorId);
    if (c.city)        result = result.filter(d =>
      d.city.toLowerCase().includes(c.city!.toLowerCase()),
    );

    if (c.sort) {
      const [col, dir] = c.sort.split(',');
      result = [...result].sort((a, b) => {
        const av = (a as any)[col];
        const bv = (b as any)[col];
        if (av === bv) return 0;
        const cmp = typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''), 'fr');
        return dir === 'desc' ? -cmp : cmp;
      });
    }

    const total = result.length;
    const page  = c.page  ?? 0;
    const size  = c.size  ?? 20;
    return { total, items: result.slice(page * size, (page + 1) * size) };
  }
}
